import { useState } from 'react';
import { flashList, type Flash } from '@/mocks/blackMaterial';
import Reveal from './Reveal';
import SectionHeading from './SectionHeading';

const FILTERS = ['全部', '实锤', '起底', '扒皮', '内幕'] as const;

const tagStyle: Record<Flash['tagColor'], string> = {
  red: 'bg-[#fdeee9] text-[#b0392a] ring-[#f0c5b6]',
  amber: 'bg-[#fbf3e0] text-[#9a6b13] ring-[#ecdcae]',
  ink: 'bg-[#ece5d8] text-[#4a4037] ring-[#d5c6ae]',
};

export default function BreakingTimeline() {
  const [active, setActive] = useState<(typeof FILTERS)[number]>('全部');
  const list = active === '全部' ? flashList : flashList.filter((f) => f.tag === active);

  return (
    <section id="rexian" className="scroll-mt-28 border-y border-[#e0d6c6] bg-[#efe7d7]/70">
      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-16">
        <Reveal>
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <SectionHeading
              kicker="BREAKING · 实锤快讯"
              title="24 小时实锤快讯流"
              desc="按信源落稿顺序滚动更新，越靠上越新。右下角热度为当前围观数，进入专题可查看逐项证据编号。"
            />
            <div className="flex flex-wrap items-center gap-2 pb-1">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => setActive(f)}
                  className={`cursor-pointer rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                    active === f
                      ? 'bg-[#262019] text-[#f5efe3]'
                      : 'bg-white text-[#6b5f51] ring-1 ring-[#e0d6c6] hover:text-[#c83424]'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="relative">
          {/* 纵轴装饰线 */}
          <div className="pointer-events-none absolute bottom-0 left-3 top-2 w-px bg-[#e0d6c6] md:left-40"></div>

          <ol className="space-y-3">
            {list.map((f, idx) => (
              <Reveal key={f.id} delay={idx * 40}>
                <li className="group relative flex flex-col gap-3 rounded-lg bg-white p-4 pl-10 ring-1 ring-[#e6dbc9] transition hover:ring-[#c83424] md:flex-row md:items-center md:gap-0 md:pl-0 md:py-3">
                  {/* 时间列 */}
                  <div className="flex items-center gap-2 md:w-40 md:shrink-0 md:justify-center">
                    <span className="absolute left-0 top-6 flex h-7 w-7 items-center justify-center rounded-full bg-[#c83424] text-white ring-4 ring-[#f6f1e8] md:static md:ml-16">
                      <i className="ri-flashlight-fill text-xs"></i>
                    </span>
                    <span className="text-sm font-bold tabular-nums text-[#262019]">{f.time}</span>
                    {f.isNew && (
                      <span className="rounded bg-[#c83424] px-1.5 py-0.5 text-[10px] font-bold text-white">NEW</span>
                    )}
                  </div>
                  {/* 内容 */}
                  <div className="flex flex-1 items-start justify-between gap-3">
                    <p className="text-sm font-medium leading-6 text-[#332c24] transition group-hover:text-[#b0392a]">
                      {f.title}
                    </p>
                    <div className="flex shrink-0 items-center gap-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tagStyle[f.tagColor]}`}
                      >
                        {f.tag}
                      </span>
                    </div>
                  </div>
                  <div className="hidden items-center gap-3 md:flex md:w-32 md:shrink-0 md:justify-end md:pr-4">
                    <span className="flex items-center gap-1 text-xs text-[#b0392a]">
                      <i className="ri-fire-fill text-[#e8603c]"></i>{f.heat}
                    </span>
                    <i className="ri-arrow-right-up-line text-[#c9bdab] transition group-hover:text-[#c83424]"></i>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>

        <p className="mt-5 flex items-center justify-center gap-1.5 text-xs text-[#8a7a67]">
          <i className="ri-loader-4-line animate-spin text-[#c83424]"></i>以上为近 24 小时滚动快讯，往后持续追更复核
        </p>
      </div>
    </section>
  );
}